import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

@Component({
  selector: 'viewer-annotation-toggle-button',
  templateUrl: './viewer-annotation-toggle-button.component.html',
  styleUrl: './viewer-annotation-toggle-button.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ViewerAnnotationToggleButtonComponent implements OnDestroy {
  @Input() isActive: boolean = false;
  @Output() clicked: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('annotationButton') annotationButton!: ElementRef<HTMLButtonElement>;
  private tooltipEl: HTMLDivElement | null = null;
  /** True while pointer/focus is over the button; used to re-show tooltip after click (no second pointerenter). */
  private isOverButton: boolean = false;

  ngOnDestroy(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  public showTooltip(): void {
    const button: HTMLButtonElement | undefined = this.annotationButton?.nativeElement;
    if (!button) {
      return;
    }

    const tooltipText: string = this.isActive ? 'Hide Annotations' : 'Display Annotations';

    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'viewer-cinema-tooltip';
      this.tooltipEl.style.position = 'fixed';
      this.tooltipEl.style.pointerEvents = 'none';
      this.tooltipEl.style.zIndex = '20000';
      this.tooltipEl.style.visibility = 'hidden';
      document.body.appendChild(this.tooltipEl);
    }

    this.tooltipEl.textContent = tooltipText;
    this.tooltipEl.style.visibility = 'visible';
    this.positionTooltip();
  }

  public hideTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    this.tooltipEl.style.visibility = 'hidden';
  }

  public onPointerEnter(): void {
    this.isOverButton = true;
    this.showTooltip();
  }

  public onPointerLeave(): void {
    this.isOverButton = false;
    this.hideTooltip();
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    const buttonRect: DOMRect = this.annotationButton.nativeElement.getBoundingClientRect();

    this.tooltipEl.style.left = `${buttonRect.left + buttonRect.width / 2}px`;
    const tooltipHeight: number = this.tooltipEl.getBoundingClientRect().height || 24;
    this.tooltipEl.style.top = `${buttonRect.top - 10 - tooltipHeight}px`;
    this.tooltipEl.style.transform = 'translateX(calc(-50% - 10px))';
  }

  public onClick(): void {
    // Layout/state can change after click; hide immediately and then re-show if we're still hovered/focused.
    this.hideTooltip();
    this.clicked.emit();
    requestAnimationFrame(() => {
      this.hideTooltip();
      requestAnimationFrame(() => {
        this.hideTooltip();
        if (this.isOverButton) {
          this.showTooltip();
        }
      });
    });
  }
}
